/**
 * React bindings of the Assets panel (W2-10 projects-data): translation through `services.i18n`, the open project's
 * DocStore (following project open / close) and a version that moves when files or their users change.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { DocStore } from '../../state/types';
export { useServicesStore } from '../../app/registry/react';

export interface AssetsText {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

export function useText(services: Pick<AppServices, 'i18n'>): AssetsText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(
    () => ({
      locale,
      t: (key, params) => i18n.t(key, params),
      tr: (ref) => i18n.tr(ref),
    }),
    [i18n, locale],
  );
}

/**
 * Changes whenever the chart's files or anything that uses them changed (assets, the song and illustration, line
 * textures, note hit sounds, effects, videos, or a whole new document).
 */
export function useAssetsVersion(store: DocStore | null): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (store === null) return undefined;
    return store.subscribe((cs) => {
      const d = cs.dirty;
      if (
        cs.source === 'load' ||
        cs.source === 'replicaResync' ||
        d.assets ||
        d.meta ||
        d.effects ||
        d.lineSet ||
        d.lineProps.size > 0 ||
        d.lineNotes.size > 0
      ) {
        setVersion((v) => v + 1);
      }
    });
  }, [store]);
  return version;
}
